import Axios from "axios";
import jwt from "jsonwebtoken";
import moment from "moment";

/**
 * Run an endpoint for real and report what came back — the TEST API button.
 *
 * The request goes straight from the browser, so a target without permissive
 * CORS headers fails here even though it would succeed server-side. That is
 * reported as what it is rather than as a broken endpoint, because the two look
 * identical from JavaScript: a blocked response has no status and no body.
 *
 * The old form never actually sent anything — it built a config object and then
 * set "success" unconditionally (RestApiForm.testRestApi), so every endpoint
 * tested green. This replaces that.
 */

const TIMEOUT_MS = 20000;

/**
 * A row's value as the request will carry it.
 *
 * A date row has no typed value — it carries a dateConfig that is resolved when
 * the request is made. Resolved here exactly as the five run-time builders do
 * it (editorSlice, boardSlice, the two widget lists, ResourceEditor), so a test
 * sends what the report will send and not something friendlier.
 *
 * That includes the gap: those builders handle daysBefore and today and have
 * never handled fixedDate, so a fixed date resolves to nothing here too. Making
 * the test fill it in would turn a param the report silently drops into one
 * that tests green.
 */
const resolveValue = (row) => {
  if (row.dataType !== "date") return row.value;

  const config = row.dateConfig;
  if (!config) return "";
  if (config.dateOption === "daysBefore") {
    return moment.utc().subtract(config.timeUnits, "day").startOf("day").format(config.dateFormat);
  }
  if (config.dateOption === "today") return moment().format(config.dateFormat);
  return "";
};

const pairsToObject = (rows) => {
  const out = {};
  (rows || []).forEach((row) => {
    const key = String(row.key || "").trim();
    if (key === "") return;
    const value = resolveValue(row);
    /* A date that resolved to nothing is left out rather than sent empty — an
       empty string is a value, and would mean something different to the
       endpoint than the param being absent. */
    if (row.dataType === "date" && (value === "" || value == null)) return;
    out[key] = value;
  });
  return out;
};

/* Auth applied as the request actually carries it. auth0 needs a token exchange
   against the authorisation server, which cannot be done from here — that one
   is reported instead of silently sent unauthenticated. */
function applyAuth(endpoint, config) {
  const { type, params = {} } = endpoint.auth || {};

  if (type === "basic") {
    const username = params.username || "";
    const password = params.password || "";
    /* Both blank means the type was picked and never filled in. Sending the
       header anyway encodes ":" and earns a 401 the user would have to work
       backwards from, so it is named here instead — the same courtesy the JWT
       branch below already extends. */
    if (username === "" && password === "") {
      return "Add a username and password before testing a Basic Auth endpoint.";
    }
    /* btoa only speaks Latin-1, so anything above U+00FF — a currency symbol, a
       non-Latin script — throws. applyAuth is called outside the request's
       try/catch, so an unguarded throw here escapes runEndpointTest entirely
       and the panel would sit on "Testing…" forever. */
    try {
      config.headers.Authorization = `Basic ${window.btoa(`${username}:${password}`)}`;
    } catch (e) {
      return "The username or password contains characters that cannot be sent in a Basic Auth header.";
    }
    return null;
  }

  if (type === "jwt") {
    const privateKey = (params.privateKey || "").replace(/\\n/g, "\n");
    if (!privateKey) return "Add a private key before testing a JWT endpoint.";
    try {
      const token = jwt.sign(
        { aud: params.aud, iss: params.iss, exp: Math.floor(Date.now() / 1000) + 1000 },
        privateKey,
        { algorithm: "RS256" },
      );
      config.headers.Authorization = `Bearer ${token}`;
      return null;
    } catch (e) {
      return `The private key could not be used to sign a token: ${e.message}`;
    }
  }

  if (type === "auth0") {
    return "OAuth endpoints cannot be tested from the browser — the token exchange has to run server-side.";
  }

  return null;
}

/* Reason phrases, because HTTP/2 carries none and axios then leaves statusText
   empty. Every endpoint this app talks to is behind an HTTP/2 proxy, so without
   this the whole message for a rejected request is the bare number "401". */
const REASON = {
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  405: "Method Not Allowed",
  408: "Request Timeout",
  409: "Conflict",
  410: "Gone",
  415: "Unsupported Media Type",
  422: "Unprocessable Entity",
  429: "Too Many Requests",
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
};

/**
 * @returns {Promise<{status: "success"|"error", message: string, detail?: string}>}
 */
export async function runEndpointTest(endpoint) {
  const url = String(endpoint.url || "").trim();
  if (url === "") return { status: "error", message: "Enter a URL first." };

  let parsed;
  try {
    parsed = new URL(url);
  } catch (e) {
    return { status: "error", message: "That is not a valid URL." };
  }
  if (["http:", "https:"].indexOf(parsed.protocol) === -1) {
    return { status: "error", message: "The URL has to be http or https." };
  }

  const config = {
    method: String(endpoint.method || "GET").toLowerCase(),
    url,
    params: pairsToObject(endpoint.params),
    headers: pairsToObject(endpoint.headers),
    timeout: TIMEOUT_MS,
  };

  const authProblem = applyAuth(endpoint, config);
  if (authProblem) return { status: "error", message: authProblem };

  const body = endpoint.body || {};
  if (body.mode === "raw" && body.rawBody) {
    config.data = body.rawBody;
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = body.rawFormat === "json" ? "application/json" : "text/plain";
    }
  } else if (body.mode === "formData") {
    config.data = pairsToObject(body.formData);
  } else if (body.mode === "urlencoded") {
    config.data = new URLSearchParams(pairsToObject(body.urlencoded)).toString();
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/x-www-form-urlencoded";
    }
  }

  try {
    const response = await Axios(config);
    return {
      status: "success",
      message: `${response.status} ${response.statusText || "OK"}`,
      detail: preview(response.data),
    };
  } catch (error) {
    if (error.response) {
      /* The endpoint answered, just not with a success code — still a real
         answer. The status and its reason phrase are the error type; the body
         underneath is where the endpoint says the rest in its own words. */
      const { status, data } = error.response;
      return {
        status: "error",
        message: `${status} ${error.response.statusText || REASON[status] || ""}`.trim(),
        detail: preview(data),
      };
    }

    if (error.code === "ECONNABORTED") {
      return { status: "error", message: `Timed out after ${TIMEOUT_MS / 1000}s` };
    }

    /* No response object at all. Three unrelated failures land here and the
       browser deliberately makes them indistinguishable from script: a blocked
       response has no status and no body, exactly like a host that never
       answered. Rule out what can be ruled out, then name the likeliest cause
       for whatever is left. */

    // Certain, and it never leaves the browser.
    if (window.location.protocol === "https:" && parsed.protocol === "http:") {
      return { status: "error", message: "Mixed content blocked" };
    }

    /* Same origin means CORS cannot be the explanation, and naming it would
       send someone after a problem they do not have. */
    if (parsed.origin === window.location.origin) {
      return { status: "error", message: "No response from the endpoint" };
    }

    /* Cross-origin: overwhelmingly CORS, but DNS and a refused connection look
       identical from here, so the label stays hedged. */
    return { status: "error", message: "Network error — likely CORS" };
  }
}

const preview = (data) => {
  if (data === null || data === undefined) return "";
  const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
  return text.length > 2000 ? `${text.slice(0, 2000)}…` : text;
};
